import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  PAGE_SIZE,
  SETTING_STATUSES,
  compareDates,
  getSettingStatus,
  parseDate,
} from "./utils";
import DatePicker from "./ui/DatePicker";
import ConfirmDialog from "./ui/ConfirmDialog";
import Pagination from "./ui/Pagination";
import "./PeriodSettingsTab.css";

/** Тексты вкладки настроек в зависимости от того, что настраивается: проект или договор */
const TARGET_TEXTS = {
  project: {
    searchPlaceholder: "Поиск по проекту...",
    allOption: "Все проекты",
    addButton: "Добавить проект",
    newTitle: "Новая настройка проекта",
    editTitle: "Редактирование настройки проекта",
    targetLabel: "Проект",
    placeholder: "Выберите проект из списка",
    allConfigured: "Все проекты уже настроены",
    deleteTitle: "Удалить настройку проекта?",
    deleteText: "Признание по проекту станет недоступно до повторной настройки периода.",
    empty: "Проекты не найдены",
  },
  contract: {
    searchPlaceholder: "Поиск по договору...",
    allOption: "Все договоры",
    addButton: "Добавить договор",
    newTitle: "Добавить договор для признания",
    editTitle: "Редактирование договора для признания",
    targetLabel: "Договор",
    placeholder: "Выберите договор из списка",
    allConfigured: "Все договоры уже добавлены",
    deleteTitle: "Удалить договор из признания?",
    deleteText: "Признание по договору станет недоступно до повторной настройки периода.",
    empty: "Договоры не найдены",
  },
};

const EMPTY_DRAFT = {
  id: null,
  targetId: "",
  organization: "",
  counterparty: "",
  startDate: "",
  endDate: "",
};

function SettingForm({ draft, texts, targetLabel, byContract, targetOptions, data, onChange, onCancel, onSave }) {
  const isEdit = Boolean(draft.id);
  const start = parseDate(draft.startDate);
  const end = parseDate(draft.endDate);
  const rangeError = start && end && end < start ? "Дата окончания раньше даты начала" : null;
  const canSave = Boolean(
    draft.targetId &&
      start &&
      end &&
      !rangeError &&
      (!byContract || (draft.organization && draft.counterparty))
  );

  const handleTargetChange = (value) => {
    if (!byContract) {
      onChange({ ...draft, targetId: value });
      return;
    }
    const contract = data.contractById[Number(value)];
    const project = data.projectById[contract?.projectId];
    onChange({
      ...draft,
      targetId: value,
      organization: contract?.organization || project?.organization || "",
      counterparty: contract?.counterparty || project?.counterparty || "",
    });
  };

  const select = (value, onSelect, placeholder, options, disabled = false) => (
    <select
      className={`rr-select rr-select--field rr-setting-form__select${value ? "" : " is-placeholder"}`}
      value={value}
      disabled={disabled}
      onChange={(event) => onSelect(event.target.value)}
    >
      <option value="" disabled>
        {placeholder}
      </option>
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );

  return (
    <div className="rr-setting-form">
      <div className="rr-setting-form__title">{isEdit ? texts.editTitle : texts.newTitle}</div>

      <div className="rr-field">
        <span className="rr-field__label">
          {targetLabel}
          <span className="rr-field__required">*</span>
        </span>
        {select(
          draft.targetId,
          handleTargetChange,
          targetOptions.length ? texts.placeholder : texts.allConfigured,
          targetOptions,
          isEdit
        )}
      </div>

      {byContract && (
        <>
          <div className="rr-field">
            <span className="rr-field__label">
              Организация<span className="rr-field__required">*</span>
            </span>
            {select(
              draft.organization,
              (value) => onChange({ ...draft, organization: value }),
              "Выберите организацию из списка",
              data.organizations.map((item) => ({ value: item, label: item }))
            )}
          </div>
          <div className="rr-field">
            <span className="rr-field__label">
              Контрагент<span className="rr-field__required">*</span>
            </span>
            {select(
              draft.counterparty,
              (value) => onChange({ ...draft, counterparty: value }),
              "Выберите контрагента из списка",
              data.counterparties.map((item) => ({ value: item, label: item }))
            )}
          </div>
        </>
      )}

      <div className="rr-setting-form__dates">
        <div className="rr-field">
          <span className="rr-field__label">
            Дата начала признания<span className="rr-field__required">*</span>
          </span>
          <DatePicker
            compact
            value={draft.startDate}
            onChange={(value) => onChange({ ...draft, startDate: value })}
            ariaLabel="Дата начала признания"
          />
        </div>
        <div className="rr-field">
          <span className="rr-field__label">
            Дата окончания признания<span className="rr-field__required">*</span>
          </span>
          <DatePicker
            compact
            value={draft.endDate}
            onChange={(value) => onChange({ ...draft, endDate: value })}
            invalid={Boolean(rangeError)}
            ariaLabel="Дата окончания признания"
          />
        </div>
      </div>
      {rangeError && <div className="rr-field__error">{rangeError}</div>}
      <div className="rr-setting-form__hint">
        Если дата начала признания не определена, механизм признания не применяется.
      </div>

      <div className="rr-setting-form__actions">
        <button type="button" className="rr-btn rr-btn--secondary" onClick={onCancel}>
          Отмена
        </button>
        <button type="button" className="rr-btn rr-btn--primary" disabled={!canSave} onClick={onSave}>
          Сохранить
        </button>
      </div>
    </div>
  );
}

function RowMenu({ onEdit, onDelete }) {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const run = (action) => () => {
    setIsOpen(false);
    action();
  };

  return (
    <div className="rr-row-menu" ref={ref}>
      <button
        type="button"
        className={`rr-row-menu__trigger${isOpen ? " is-open" : ""}`}
        aria-label="Действия"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <svg width="16" height="4" viewBox="0 0 16 4" fill="currentColor" aria-hidden>
          <circle cx="2" cy="2" r="1.5" />
          <circle cx="8" cy="2" r="1.5" />
          <circle cx="14" cy="2" r="1.5" />
        </svg>
      </button>
      {isOpen && (
        <div className="rr-row-menu__list" role="menu">
          <button type="button" role="menuitem" onClick={run(onEdit)}>
            Редактировать
          </button>
          <button type="button" role="menuitem" className="is-danger" onClick={run(onDelete)}>
            Удалить
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Настройки периодов признания.
 * config.periodBy === "project" — настройка { projectId, startDate, endDate },
 * "contract" — { contractId, organization, counterparty, startDate, endDate }.
 */
function PeriodSettingsTab({ config, data, settings, onChange }) {
  const byContract = config.periodBy === "contract";
  const texts = TARGET_TEXTS[config.periodBy];
  const targetKey = byContract ? "contractId" : "projectId";
  const getTargetName = (setting) =>
    byContract
      ? data.contractById[setting.contractId].name
      : data.projectById[setting.projectId].name;

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [draft, setDraft] = useState(null);
  const [settingToDelete, setSettingToDelete] = useState(null);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return settings
      .filter((setting) => {
        if (status && getSettingStatus(setting) !== status) return false;
        if (query) {
          const name = byContract
            ? `${data.contractById[setting.contractId]?.name || ""} ${setting.counterparty}`
            : data.projectById[setting.projectId]?.name || "";
          if (!name.toLowerCase().includes(query)) return false;
        }
        return true;
      })
      .sort((a, b) => compareDates(b.startDate, a.startDate) || String(b.id).localeCompare(String(a.id)));
  }, [settings, search, status, byContract, data]);

  useEffect(() => {
    setPage(1);
  }, [search, status]);

  const configuredIds = settings.map((setting) => setting[targetKey]);
  const targetOptions = (byContract ? data.contracts : data.projects)
    .filter((item) => !configuredIds.includes(item.id) || item.id === Number(draft?.targetId))
    .map((item) => ({ value: String(item.id), label: item.name }));

  const handleSave = () => {
    const payload = {
      [targetKey]: Number(draft.targetId),
      startDate: draft.startDate,
      endDate: draft.endDate,
      ...(byContract ? { organization: draft.organization, counterparty: draft.counterparty } : {}),
    };
    if (draft.id) {
      onChange(settings.map((item) => (item.id === draft.id ? { ...item, ...payload } : item)));
    } else {
      const numericIds = settings
        .map((item) => Number(item.id))
        .filter((id) => Number.isFinite(id));
      const id = (numericIds.length ? Math.max(...numericIds) : 0) + 1;
      onChange([{ id, ...payload }, ...settings]);
    }
    setDraft(null);
  };

  const handleDelete = () => {
    onChange(settings.filter((item) => item.id !== settingToDelete.id));
    if (draft?.id === settingToDelete.id) setDraft(null);
    setSettingToDelete(null);
  };

  const startEdit = (setting) =>
    setDraft({
      ...EMPTY_DRAFT,
      ...setting,
      targetId: String(setting[targetKey]),
    });

  const rows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const noun = config.texts.nounGenitive;

  return (
    <>
      <div className="rr-filters">
        <label className="rr-search">
          <svg className="rr-search__icon" width="18" height="18" viewBox="0 0 18 18" fill="none">
            <circle cx="8" cy="8" r="5.5" stroke="currentColor" strokeWidth="1.4" />
            <path d="M12.5 12.5L16 16" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
          </svg>
          <input
            className="rr-search__input"
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={texts.searchPlaceholder}
          />
        </label>
        <select
          className={`rr-select${status ? " is-active" : ""}`}
          value={status}
          onChange={(event) => setStatus(event.target.value)}
        >
          <option value="">{texts.allOption}</option>
          {Object.entries(SETTING_STATUSES).map(([key, item]) => (
            <option key={key} value={key}>
              {item.label}
            </option>
          ))}
        </select>
      </div>

      <div className="rr-settings__toolbar">
        <button
          type="button"
          className="rr-btn rr-btn--primary"
          disabled={Boolean(draft) && !draft.id}
          onClick={() => setDraft(EMPTY_DRAFT)}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M8 3.333V12.667M3.333 8H12.667" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
          {texts.addButton}
        </button>
      </div>

      {draft && (
        <SettingForm
          key={draft.id || "new"}
          draft={draft}
          texts={texts}
          targetLabel={byContract ? `Договор для признания ${noun}` : `Проект для признания ${noun}`}
          byContract={byContract}
          targetOptions={targetOptions}
          data={data}
          onChange={setDraft}
          onCancel={() => setDraft(null)}
          onSave={handleSave}
        />
      )}

      <div>
        <table className={`rr-table rr-settings${byContract ? " rr-settings--contracts" : ""}`}>
          <thead>
            <tr>
              <th>{texts.targetLabel}</th>
              {byContract && (
                <>
                  <th>Организация</th>
                  <th>Контрагент</th>
                </>
              )}
              <th>Дата начала признания {noun}</th>
              <th>Дата окончания признания {noun}</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((setting) => {
              const statusInfo = SETTING_STATUSES[getSettingStatus(setting)];
              return (
                <tr key={setting.id} className={draft?.id === setting.id ? "is-editing" : undefined}>
                  <td className="rr-settings__target">
                    <span>{getTargetName(setting)}</span>
                    <span className={`rr-status ${statusInfo.className}`}>{statusInfo.label}</span>
                  </td>
                  {byContract && (
                    <>
                      <td className="rr-settings__party">{setting.organization}</td>
                      <td className="rr-settings__party">{setting.counterparty}</td>
                    </>
                  )}
                  <td className="rr-settings__date">{setting.startDate || "—"}</td>
                  <td className="rr-settings__date">{setting.endDate || "—"}</td>
                  <td className="rr-settings__actions">
                    <RowMenu onEdit={() => startEdit(setting)} onDelete={() => setSettingToDelete(setting)} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {rows.length === 0 && <div className="rr-empty-state">{texts.empty}</div>}
      </div>

      <Pagination page={page} pageSize={PAGE_SIZE} total={filtered.length} onChange={setPage} />

      <ConfirmDialog
        isOpen={Boolean(settingToDelete)}
        themeClass={config.themeClass}
        title={texts.deleteTitle}
        text={texts.deleteText}
        rows={
          settingToDelete
            ? [
                { label: texts.targetLabel, value: getTargetName(settingToDelete) },
                {
                  label: "Период",
                  value: `${settingToDelete.startDate} — ${settingToDelete.endDate}`,
                },
              ]
            : []
        }
        confirmLabel="Удалить"
        danger
        onConfirm={handleDelete}
        onCancel={() => setSettingToDelete(null)}
      />
    </>
  );
}

export default PeriodSettingsTab;
